// Auth context — holds the current user, exposes login/logout, gates the rest
// of the app. The session itself lives in an httpOnly cookie set by FastAPI,
// so a fresh tab boot calls /api/auth/me to re-hydrate the session.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { API, apiGet, apiPost, setUnauthenticatedHandler } from "../lib/api";
import { trackEvent } from "../lib/analytics";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState("loading"); // loading | authed | anon

    // Server says session is gone → flip state. Components reading `status`
    // will re-render and Guards will redirect to /login automatically.
    useEffect(() => {
        setUnauthenticatedHandler(() => {
            setUser(null);
            setStatus("anon");
        });
        return () => setUnauthenticatedHandler(null);
    }, []);

    const refresh = useCallback(async () => {
        const { data, status: code } = await apiGet(`${API}/auth/me`);
        if (code === 200 && data?.user) {
            setUser(data.user);
            setStatus("authed");
        } else {
            setUser(null);
            setStatus("anon");
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (username, password) => {
        const { data, error, status: code } = await apiPost(`${API}/auth/login`, { username, password });
        if (code === 200 && data?.user) {
            setUser(data.user);
            setStatus("authed");
            // Analytics: PII-free — role only, never the username.
            trackEvent("login_success", { role: data.user.role || "unknown" });
            return { ok: true };
        }
        trackEvent("login_failure", { status_code: code || 0 });
        return { ok: false, error: error || "Login failed" };
    }, []);

    const logout = useCallback(async () => {
        await apiPost(`${API}/auth/logout`);
        trackEvent("logout", {});
        setUser(null);
        setStatus("anon");
    }, []);

    const isAdmin = !!user && user.role === "admin";
    const canMarket = !!user && (isAdmin || (user.allowedViews || []).includes("market"));
    const canClient = !!user && (isAdmin || (user.allowedViews || []).includes("client"));

    const canAccessClient = useCallback((clientId) => {
        if (!user) return false;
        if (user.role === "admin") return true;
        const allowed = (user.allowedClientIds || []).map((c) => String(c).toLowerCase());
        return allowed.includes(String(clientId || "").toLowerCase());
    }, [user]);

    const value = useMemo(() => ({
        user, status, login, logout, refresh,
        isAdmin, canMarket, canClient, canAccessClient,
    }), [user, status, login, logout, refresh, isAdmin, canMarket, canClient, canAccessClient]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};
